import Image from "next/image";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { CHAIN } from "@/v2-components/pages/games/vr-overview/vr-ath-band";
import ViewportVideo from "@/v2-components/pages/games/viewport-video";
import CloseIcon from "@/v2-components/core/icon/close-icon";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/v2-components/ui/sheet";

/**
 * 홈 「Feel the Game Across Every Way You Play」의 **Audio-to-Haptics 팝업** (#5516).
 *
 * 시안 데스크탑 `30255:56620`(960×636) · 모바일 `30255:56734`(360 바텀시트).
 *
 * ## 종전엔 페이지로 나갔다
 *
 * `Console Games` · `Music & Movies` 두 카드가 `/games/audio-to-haptic` 으로 **이동**했다.
 * 작성자 요청으로 **팝업**으로 바꾼다 — 홈에서 맥락을 잃지 않고 설명만 보여 준다.
 *
 * ## ★`ui/sheet` 하나로 두 폭을 만든다
 *
 * 그 컴포넌트의 `side` 에 **`bottom` 과 `center` 가 둘 다** 있다 — 모바일은 아래에서
 * 올라오는 시트, 데스크탑은 가운데 다이얼로그다. 슬라이드 애니메이션·`rounded-t-2xl`·
 * 오버레이·포커스 트랩이 거기 이미 있어서, `Dialog` 로 위치를 손수 잡으면 그것들을
 * 다시 구현하게 된다(작성자 지적 2026-09-04).
 *
 * ⚠️ `SheetHeader` 는 쓰지 않는다 — 기본 `-mt-4`/`py-5` 가 있어 커스텀 패딩과 겹친다.
 *
 * ## ★문구는 `CHAIN` 을 재사용한다 — 미디어만 이 화면 값이다
 *
 * 3항목의 제목·설명이 VR ATH 밴드(`vr-ath-band.tsx` 의 `CHAIN`)와 **글자 단위로 같다**
 * (시안 대조). 그 상수는 #5533 에서 이미 export 돼 있고 7로케일 해석이
 * `vr-games-keys-resolve` 로 잠겨 있다 — 여기서 문구를 다시 적으면 두 벌이 갈라진다.
 *
 * ⚠️ 그 키들은 **`games` ns** 다. 홈은 종전에 `index`·`gnb`·`tactsuit` 만 SSR 로 실어서
 * 한국어에서 영문이 그대로 나갔다 — `src/pages/index.tsx` 에 `games` 를 더했다.
 *
 * ⚠️ **미디어는 공유하지 않는다.** 세 번째 항목이 두 화면에서 다르다(시안 실측):
 *
 * ```
 *   VR ATH 밴드 `30168:156189`   VR 착용 사진   → ath-item-vibration.webp (리포와 일치)
 *   이 팝업     `30255:56641`    액추에이터     → ath_responsive-haptics.mp4
 * ```
 *
 * 작성자 메모가 *"`/games/pc` 에 넣어주셨던 영상 그대로 활용"* 이라 그 영상을 쓴다 —
 * `PcAiBand` 의 「Vibration Motors Bring It to Life」와 **같은 파일**이다. 그래서 공유
 * 상수(`CHAIN.image`)를 고치면 VR 페이지가 틀어진다.
 */

/** 시안 `30255:56631`·`56636`·`56641` — 항목 미디어. 순서는 `CHAIN` 과 같다. */
const MEDIA: ({ image: string } | { video: string })[] = [
  { image: "/images/games/vr-overview/ath-item-sound.webp" },
  { image: "/images/games/vr-overview/ath-item-analysis.webp" },
  // ★영상 — `PcAiBand` 와 같은 파일(작성자 메모).
  { video: "https://cdn.bhaptics.com/media/ath_responsive-haptics.mp4" },
];

export default function AudioToHapticDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  /**
   * ★**ns 는 훅 하나에 하나씩이다 — 배열로 둘을 주면 첫 번째만 찾는다.**
   *
   * `useTrans(["index", "games"])` 로 한 번에 부르면 `index` 키만 해석되고 `games`
   * 키는 **키 문자열이 그대로 화면에 나간다**(2026-09-04 실측: 같은 팝업에서 제목은
   * `모든 콘텐츠의 소리를 햅틱으로`, 3항목은 `Detects Sound`). 스토어에는 두 ns 가
   * 다 들어 있어서(`__NEXT_DATA__` 의 `initialI18nStore.ko.games` 438키, 값도 국문)
   * **사전 누락처럼 보이지 않는다** — 그래서 조용하다.
   *
   * 리포 전체에서 `useTrans` 를 ns 2개 이상으로 부르는 자리가 **0건**이고 ns 접두사
   * 표기(`t("games:…")`)도 **0건**이다. 관습대로 훅을 나눈다.
   */
  const { t } = useTrans(["index"]);
  /** `CHAIN` 의 제목·설명은 `games` ns 다(`vr-ath-band.tsx`). */
  const { t: tGames } = useTrans(["games"]);
  /**
   * `laptop`(1024) 위는 가운데 다이얼로그, 아래는 바텀시트.
   *
   * ⚠️ SSR 에서는 `false` 라 **바텀시트로 먼저 그려진다.** 팝업은 사용자가 누른 뒤에만
   * 열리므로 그 시점엔 이미 클라이언트다 — 깜빡임이 보이지 않는다.
   */
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? "center" : "bottom"}
        className={cn(
          // 기본 variant 가 밝은 배경이라 덮는다. 시안은 검정 + 오로라다.
          //
          // ★`laptop:p-0` 을 **따로** 적는다. `sheetVariants` 기본값이
          // `p-4 laptop:p-8` 이라 `p-0` 하나로는 laptop 쪽이 안 지워진다 —
          // tailwind-merge 는 **같은 breakpoint 안에서만** 충돌을 푼다(pitfalls #8).
          // 실측(2026-09-04 dev 1440): 이 클래스가 없으면 좌우 32px 이 더 붙어
          // 항목이 시안 280 이 아니라 **267** 로 눌린다.
          "flex flex-col overflow-hidden bg-black p-0 text-white laptop:p-0",
          // 모바일 — 아래에서 올라오는 시트(높이는 내용에 맡기되 화면을 넘지 않게).
          "h-auto max-h-[90dvh] rounded-t-2xl",
          // 데스크탑 — 시안 960 다이얼로그.
          "laptop:h-auto laptop:max-h-[90vh] laptop:w-[960px] laptop:max-w-[calc(100vw-64px)] laptop:rounded-2xl",
        )}
      >
        {/* 배경 — 시안 `30255:56620` 의 오로라. 내용 뒤에 깔린다. */}
        <Image
          src="/images/home/ath-dialog/backdrop.webp"
          alt=""
          aria-hidden
          fill
          sizes="(min-width: 1024px) 960px, 100vw"
          className="object-cover"
        />

        {/**
         * 모바일 grabber — 시안 `30255:89431` 40×4. 데스크탑엔 없다.
         *
         * ★**스크롤러 밖**이다. 안에 두면 내용과 같이 밀려 올라가 사라진다(2026-09-04
         * 실측: 360 폭에서 내부 스크롤 1305/666 이라 실제로 스크롤이 생긴다).
         * `SheetContent` 를 `flex flex-col` 로 두고 grabber 는 `shrink-0`, 스크롤러가
         * 남은 높이를 먹는다(`min-h-0 flex-1` — 없으면 flex 자식이 안 줄어 스크롤이 안 생긴다).
         */}
        <div
          aria-hidden
          className="relative mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-white/40 laptop:hidden"
        />

        {/**
         * 닫기 — 시안 `30255:56623` 24px 우상단. **데스크탑 전용**이다.
         *
         * 모바일에선 grabber 가 그 자리를 대신하고, 오버레이 탭·ESC·뒤로가기로 닫힌다
         * (작성자 요청 2026-09-04). 리포의 다른 바텀시트도 `PartnerFilter` 는 닫기
         * 버튼이 0곳이라 이 쪽이 다수는 아니어도 예외는 아니다.
         *
         * `SheetClose`(Radix `Close`)를 쓴다 — 포커스 복귀가 그쪽에 붙어 있다.
         */}
        <SheetClose
          aria-label={t("Close")}
          className="absolute right-6 top-6 z-10 hidden text-white laptop:block"
        >
          <CloseIcon className="size-6" />
        </SheetClose>

        <div className="relative min-h-0 flex-1 overflow-y-auto px-5 pb-10 pt-6 laptop:px-6 laptop:pb-10 laptop:pt-6">
          {/* ── 헤더 `30255:56625` — 갭 24 · 가운데 ── */}
          <div className="flex flex-col items-center gap-3 text-center laptop:gap-6">
            {/* `30255:56626` — 14/18 SemiBold tr.25 #ffd600 */}
            <p className="text-base font-600 tracking-[0.25px] text-bhaptics-yellow">
              {t("AUDIO TO HAPTIC")}
            </p>
            {/* `30255:56627` — 36/44 white. 모바일은 24/32. */}
            <SheetTitle className="text-3xl font-400 text-white laptop:text-6xl">
              {t("Turn Every Content Sound into Haptics")}
            </SheetTitle>
            {/**
             * `30255:56628` — 16/22 tr.5 #dde1e6
             *
             * ★평범한 `<p>` 가 아니라 `SheetDescription`(Radix `Description`)이다.
             * 그게 없으면 Radix 가 `Missing Description or aria-describedby` 를 경고하고,
             * 스크린리더가 제목만 읽어 팝업이 무엇인지 안 들린다(2026-09-04 콘솔 실측).
             */}
            <SheetDescription className="text-sm tracking-[0.4px] text-coolgray-20 laptop:text-lg laptop:tracking-[0.5px]">
              {t(
                "Feel Games, Movies, Music outside the supported list through sound.",
              )}
            </SheetDescription>
          </div>

          {/**
           * ── 3항목 `30255:56629` ──
           *
           * 데스크탑은 가로 3열(항목 280 · `justify-between`), 모바일은 세로 스택이다
           * (시안 `30255:56739` — 항목 280 · 간격 40).
           */}
          <div className="mt-10 flex flex-col gap-10 laptop:flex-row laptop:justify-between laptop:gap-6">
            {CHAIN.map((step, i) => {
              const media = MEDIA[i];
              return (
                <div
                  key={step.title}
                  className="flex flex-col items-center gap-4 laptop:w-[280px] laptop:gap-6"
                >
                  {/* 시안 `30255:56631` — 280×240 · radius 40 */}
                  <div className="relative aspect-[280/240] w-full overflow-hidden rounded-[40px]">
                    {"video" in media ? (
                      <ViewportVideo
                        src={media.video}
                        loop
                        className="absolute inset-0 size-full object-cover"
                      />
                    ) : (
                      <Image
                        src={media.image}
                        alt=""
                        aria-hidden
                        fill
                        sizes="(min-width: 1024px) 280px, 100vw"
                        className="object-cover"
                      />
                    )}
                  </div>
                  {/**
                   * 시안 `30255:56632` — 갭 8 · 가운데 · 그림자로 배경과 분리한다
                   * (`text-shadow 0 2 16 rgba(0,0,0,.72)`).
                   */}
                  <div className="flex flex-col gap-2 text-center [text-shadow:0_2px_16px_rgba(0,0,0,0.72)]">
                    <p className="text-lg font-500 tracking-[0.15px] text-white">
                      {tGames(step.title)}
                    </p>
                    <p className="text-base tracking-[0.25px] text-white/[72%]">
                      {tGames(step.desc)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
